/**
 * Ocean Data Service
 * Handles loading, processing, and validation of oceanographic data from the BlueTide data-proxy API.
 */

/**
 * Host for POST …/usmcom/data-proxy/query (no trailing slash).
 * If REACT_APP_BASE_URL is unset, development defaults to staging so local `npm start` loads data without a .env.
 */
export function getBluetideDataProxyBaseUrl() {
  const raw = process.env.REACT_APP_BASE_URL;
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'development') {
    return 'https://api-staging.isdata.ai';
  }
  return '';
}

const resolvedDataProxyBaseUrl = getBluetideDataProxyBaseUrl();

// API Configuration
if (process.env.NODE_ENV === 'production' && resolvedDataProxyBaseUrl && !resolvedDataProxyBaseUrl.startsWith('https://')) {
  console.warn('Insecure API endpoint configured for production environment. Please use https.');
}

const API_CONFIG = {
  baseUrl: resolvedDataProxyBaseUrl,
  endpoint: process.env.REACT_APP_DATA_PROXY_ENDPOINT || '/usmcom/data-proxy/query',
  timeout: 600000, // 10 minutes
  retries: 2,
  token: process.env.REACT_APP_BEARER_TOKEN
};

// BlueTide-specific configuration (trim; treat "" as unset — same as aiService)
const trimEnvOr = (value, fallback) => {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s !== '' ? s : fallback;
};
const BLUETIDE_CONFIG = {
  tenantUuid: trimEnvOr(process.env.REACT_APP_BLUETIDE_TENANT_UUID, '79f79901-6f6b-49cc-927e-e7533763a7cc'),
  userUuid: trimEnvOr(process.env.REACT_APP_BLUETIDE_USER_UUID, '4158fe99-3866-4f18-96f4-992da2aa51f9'),
  project: process.env.REACT_APP_BQ_PROJECT || 'isdata-staging',
  dataset: process.env.REACT_APP_BQ_DATASET || '79f79901_6f6b_49cc_927e_e7533763a7cc'
};

/** Headers for POST /data-proxy/query. Some gateways accept tenant + user only; Bearer is added when set. */
function buildDataProxyRequestHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'x-bluemvmt-tenant-uuid': BLUETIDE_CONFIG.tenantUuid,
    'x-bluemvmt-user-uuid': BLUETIDE_CONFIG.userUuid
  };
  const token = API_CONFIG.token;
  if (token != null && String(token).trim() !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// BlueTide datasource UUID mappings (from bluetide-data-proxy-doc.md)
export const BLUETIDE_DATASOURCES = {
  'subsurface_vessel_data': '7646f432-265e-4551-8b8b-f887cdba6baa',
  'synthetic_subsurface_data': '026ad63b-bb56-42dc-9d19-4575f15c6a2d',
  's_acoustic': '72067785-323d-45d6-bdbe-b70005ec20d5',
  'n_wmo_wind_direction_speed': '956993b1-30ed-4737-9eed-4583892ceb88',
  'n_wmo_airpressure': 'de73c2fe-a255-4e1e-9394-a7e39d66aa2c',
  'highres_imaging': '80c8e91b-fb0b-4124-825b-28050d6e5f69',
  'n_wmo_surface_wave': '92f13dff-4913-46c3-83b4-780e64ff740f',
  'synthetic_acoustic_data': '52d08621-918c-43c0-9460-af2e2e00acf4',
  'n_wmo_air_temp': '6df37e19-0d95-4a10-b321-8ad15a722846',
  // Default: Awarion-style detections table (see postman / historical staging).
  // Official proxy doc lists surface_vessel_data.csv as 9485d6af-… — override if your tenant uses that table:
  // REACT_APP_BLUETIDE_SURFACE_VESSEL_DATASOURCE_UUID
  'surface_vessel_data':
    process.env.REACT_APP_BLUETIDE_SURFACE_VESSEL_DATASOURCE_UUID || '4929ea15-887c-4fb4-a023-dbf216b3be70',
  'n_wmo_surface_temp': '4b8df973-a057-472b-af4a-f35113ff412b',
  'imaging_data_regenerated': '112ded45-9257-4b6b-904e-8efbe83b8aec',
  'imaging_data': 'bcaaa0b2-eff6-4c0a-ae7a-b067f86c015a',
  'surface_vessel_ops': '240f2ad0-3869-409a-814f-8f22967a81da',
  'n_wmo_wave_density': '0aba5e68-456d-47e1-bac3-8d4b1b81766e',
  'subsurface_vessel_ops': 'd60cd7b2-03a9-4f69-8e13-f39429ec132d',
  'surface_vessel_data_regenerated': 'fc0c2526-015c-4c9b-be5a-2806b9a1f340',
  'n_wmo_wave_vector_height': 'bb048685-1ac0-4c2c-b6e2-a5abee149331',
  'synthetic_acoustic': 'ceaaa3ce-2f2d-4a94-a1f1-835fdf2a9d5b',
  'subsurface_vessel_data_regenerated': '41eea2f3-27eb-4a55-a4fd-dd1def67bc8d'
};

// WMO Datasource Schema (shares common structure)
const WMO_SCHEMA = {
  columns: ['*'],
  timestampField: 'time_utc',
  fieldMap: {
    time: 'time_utc'
  }
};

// Datasource column schemas (discovered via SELECT * LIMIT 1)
const DATASOURCE_SCHEMAS = {
  'surface_vessel_data': {
    // Awarion detections table `4929ea15_887c_4fb4_a023_dbf216b3be70` — explicit SELECT matches
    // POST https://api-staging.isdata.ai/usmcom/data-proxy/query { "sql": "SELECT … FROM `…4929…` …" }
    columns: [
      'unique_identifier',
      'common_name',
      'location_latitude',
      'location_longitude',
      'location_depth_meters',
      'timestamp',
      'message_description_label',
      'message_description_name',
      'message_sensor_source_id',
      'message_threat_level',
      'message_range',
      'message_bearing',
      'message_velocity_x',
      'message_velocity_y',
      'message_velocity_z',
      'message_speed_over_ground',
      'message_course_over_ground',
      'message_dimensions_x',
      'message_dimensions_y',
      'message_dimensions_z',
      'water_salinity_psu',
      'water_temperature_c'
    ],
    timestampField: 'timestamp',
    fieldMap: {
      lat: 'location_latitude',
      lon: 'location_longitude',
      time: 'timestamp',
      depth: 'location_depth_meters',
      speed: 'message_speed_over_ground',
      direction: 'message_course_over_ground',
      common_name: 'common_name'
    }
  },
  'subsurface_vessel_data': {
    columns: ['timestamp', 'latitude', 'longitude', 'depth_m', 'acoustic_signature_level', 'sonar_contact_strength', 'threat_confirmed'],
    timestampField: 'timestamp',
    fieldMap: {
      lat: 'latitude',
      lon: 'longitude',
      time: 'timestamp',
      depth: 'depth_m'
    }
  },
  's_acoustic': {
    columns: ['acoustic_intensity', 'dominant_frequency_hz', 'time_utc'],
    timestampField: 'time_utc',
    fieldMap: {
      time: 'time_utc'
    }
  },
  'highres_imaging': {
    columns: ['timestamp', 'frame_id', 'grid_x', 'grid_y', 'intensity', 'object_classification', 'object_speed_knots', 'object_heading_deg', 'sensor_platform'],
    timestampField: 'timestamp',
    fieldMap: {
      time: 'timestamp',
      speed: 'object_speed_knots',
      direction: 'object_heading_deg'
    }
  },
  // WMO Datasources
  'n_wmo_wind_direction_speed': WMO_SCHEMA,
  'n_wmo_airpressure': WMO_SCHEMA,
  'n_wmo_surface_wave': WMO_SCHEMA,
  'n_wmo_air_temp': WMO_SCHEMA,
  'n_wmo_surface_temp': WMO_SCHEMA,
  'n_wmo_wave_density': WMO_SCHEMA,
  'n_wmo_wave_vector_height': WMO_SCHEMA
};

// Default schema for unknown datasources (uses SELECT *)
const DEFAULT_SCHEMA = {
  columns: ['*'],
  timestampField: 'timestamp',
  fieldMap: {}
};

/**
 * Gets the schema for a datasource
 * @param {string} datasourceName - The datasource name
 * @returns {object} Schema with columns, timestampField, and fieldMap
 */
const getSchemaForDatasource = (datasourceName) => {
  return DATASOURCE_SCHEMAS[datasourceName] || DEFAULT_SCHEMA;
};

/**
 * Display labels for datasources in the Control Panel UI
 */
const DATASOURCE_DISPLAY_LABELS = {
  'surface_vessel_data': 'Surface Vessel Data',
  'subsurface_vessel_data': 'Subsurface Vessel Data',
  'synthetic_subsurface_data': 'Synthetic Subsurface Data',
  's_acoustic': 'Acoustic Data',
  'synthetic_acoustic': 'Synthetic Acoustic Data',
  'synthetic_acoustic_data': 'Synthetic Acoustic Preview',
  'highres_imaging': 'High-Res Imaging',
  'imaging_data': 'Imaging Data',
  'imaging_data_regenerated': 'Imaging Data (Regenerated)',
  'n_wmo_wind_direction_speed': 'WMO Wind Direction/Speed',
  'n_wmo_airpressure': 'WMO Air Pressure',
  'n_wmo_surface_wave': 'WMO Surface Wave',
  'n_wmo_air_temp': 'WMO Air Temperature',
  'n_wmo_surface_temp': 'WMO Surface Temperature',
  'n_wmo_wave_density': 'WMO Wave Density',
  'n_wmo_wave_vector_height': 'WMO Wave Vector Height',
  'surface_vessel_ops': 'Surface Vessel Operations',
  'subsurface_vessel_ops': 'Subsurface Vessel Operations',
  'surface_vessel_data_regenerated': 'Surface Vessel Data (Regenerated)',
  'subsurface_vessel_data_regenerated': 'Subsurface Vessel Data (Regenerated)'
};

/**
 * Returns list of available datasources for the Control Panel
 * @returns {Array<{key: string, label: string, uuid: string, hasDepth: boolean, hasLatLon: boolean}>}
 */
export const getAvailableDatasources = () => {
  return Object.entries(BLUETIDE_DATASOURCES).map(([key, uuid]) => {
    const schema = getSchemaForDatasource(key);
    const hasDepth = schema.columns?.some(col => col.toLowerCase().includes('depth')) || false;
    const hasLatLon = schema.fieldMap?.lat && schema.fieldMap?.lon;

    return {
      key,
      label: DATASOURCE_DISPLAY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      uuid,
      hasDepth,
      hasLatLon
    };
  });
};

/**
 * Converts a UUID to BigQuery-safe format (hyphens to underscores)
 * @param {string} uuid - Standard UUID with hyphens
 * @returns {string} UUID with underscores for BigQuery table names
 */
const uuidToBqFormat = (uuid) => uuid.replace(/-/g, '_');

/**
 * Gets the full BigQuery table ID for a datasource
 * @param {string} datasourceName - The datasource name (e.g., 'surface_vessel_data')
 * @returns {string} Full table ID in format: project.dataset.table_uuid
 */
const getTableIdForDatasource = (datasourceName) => {
  const uuid = BLUETIDE_DATASOURCES[datasourceName] || BLUETIDE_DATASOURCES['surface_vessel_data'];
  const tableUuid = uuidToBqFormat(uuid);
  return `${BLUETIDE_CONFIG.project}.${BLUETIDE_CONFIG.dataset}.${tableUuid}`;
};

/** Prefer stable UI field `unique_identifier` after API column renames. */
function coalesceVesselUniqueIdentifier(row) {
  if (!row || typeof row !== 'object') return null;
  const candidates = [
    row.unique_identifier,
    row.vessel_uuid,
    row.track_uuid,
    row.asset_uuid,
    row.platform_uuid,
    row.vessel_id,
    row.detection_id
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c);
  }
  return null;
}

/**
 * Maps ocean area names to BlueTide datasource names
 * @param {string} areaName - The selected ocean area
 * @returns {string} The corresponding datasource name
 */
const getTableNameForArea = (areaName) => {
  // Now areaName is actually the datasource key directly
  if (BLUETIDE_DATASOURCES[areaName]) {
    return areaName;
  }
  // Default to surface_vessel_data if unknown datasource
  console.warn(`Unknown datasource "${areaName}", defaulting to surface_vessel_data`);
  return 'surface_vessel_data';
};

/**
 * Loads data from the BlueTide data-proxy API based on specified query parameters.
 * Uses POST requests with SQL in JSON body per the BlueTide API specification.
 * @param {object} queryParams - The query parameters for filtering data.
 * @param {string} queryParams.datasource - The BlueTide datasource key (e.g., 'surface_vessel_data').
 * @param {Date} queryParams.startDate - The start of the selected date/time range.
 * @param {Date} queryParams.endDate - The end of the selected date/time range.
 * @returns {Promise<{allData: Array, totalRows: number}>} A promise that resolves to an object
 * containing all the data rows from the API and the total row count.
 */
export const loadAllData = async (queryParams = {}) => {
  const {
    area: selectedArea = 'USM',
    datasource,
    startDate,
    endDate
  } = queryParams;

  // Determine datasource: use explicit datasource if provided, otherwise map from area
  const datasourceName = datasource || getTableNameForArea(selectedArea);
  const tableId = getTableIdForDatasource(datasourceName);
  const schema = getSchemaForDatasource(datasourceName);

  // Build SQL query with schema-aware columns
  const columnsClause = schema.columns.join(', ');
  const baseQuery = `SELECT ${columnsClause} FROM \`${tableId}\``;
  const whereClauses = [];

  if (startDate && endDate) {
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();
    whereClauses.push(`${schema.timestampField} BETWEEN TIMESTAMP('${startISO}') AND TIMESTAMP('${endISO}')`);
  }

  let sql = baseQuery;
  if (whereClauses.length > 0) {
    sql += ` WHERE ${whereClauses.join(' AND ')}`;
  }
  sql += ` ORDER BY ${schema.timestampField} DESC LIMIT 10000`;

  try {
    if (!API_CONFIG.baseUrl) {
      console.error(
        'BlueTide: REACT_APP_BASE_URL is not set (required in production). Example: https://api-staging.isdata.ai'
      );
      return { allData: [], totalRows: 0 };
    }

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`;

    // BlueTide data-proxy uses POST with JSON body containing SQL
    const response = await fetch(url, {
      method: 'POST',
      headers: buildDataProxyRequestHeaders(),
      body: JSON.stringify({ sql })
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorDetail = errorBody.detail || response.statusText;
      const errorType = errorBody.error_type || 'http_error';
      throw new Error(`[${errorType}] HTTP ${response.status}: ${errorDetail}`);
    }

    const apiData = await response.json();



    // BlueTide API returns {rows: [...], total_rows: N}
    const rows = apiData.rows || [];
    const totalRows = apiData.total_rows || rows.length;

    // Normalize row data: map BlueTide columns to UI-expected field names
    const fieldMap = schema.fieldMap;
    const allData = rows.map((row, index) => {
      const normalized = {
        // Map known fields using fieldMap (reverse mapping: UI field <- API field)
        lat: row[fieldMap.lat] ?? row.latitude ?? row.lat ?? null,
        lon: row[fieldMap.lon] ?? row.longitude ?? row.lon ?? null,
        time: row[fieldMap.time] ?? row.timestamp ?? row.time_utc ?? null,
        direction: row[fieldMap.direction] ?? row.heading_deg ?? row.direction ?? null,
        speed: row[fieldMap.speed] ?? row.speed_knots ?? row.speed ?? null,
        nspeed: row[fieldMap.speed] ?? row.speed_knots ?? row.nspeed ?? null,
        depth: row[fieldMap.depth] ?? row.depth_m ?? row.depth ?? null,
        // Preserve all original fields
        ...row,
        // Explicit common_name (vessel category) so it is always available for tooltips/filters
        common_name: fieldMap.common_name != null ? (row[fieldMap.common_name] ?? row.common_name ?? null) : (row.common_name ?? null),
        // Metadata
        model: 'BlueTide',
        area: selectedArea,
        datasource: datasourceName,
        _source_file: `BlueTide_${datasourceName}`,
        _loaded_at: new Date().toISOString()
      };

      // Alias alternate API column names (e.g. some grids use water_* ) to UI keys.
      normalized.temp =
        normalized.temp ??
        normalized.temperature ??
        normalized.water_temperature_c ??
        null;
      normalized.salinity =
        normalized.salinity ??
        normalized.water_salinity_psu ??
        null;
      normalized.depth =
        normalized.depth ??
        normalized.water_depth_m ??
        normalized.water_depth_meters ??
        null;

      // Normalize any existing threat_detected field (numeric/boolean/string) first.
      if (normalized.threat_detected !== undefined && normalized.threat_detected !== null) {
        const raw = normalized.threat_detected;
        if (raw === true || raw === 'Yes' || raw === 'yes') {
          normalized.threat_detected = 'Yes';
        } else {
          const num = Number(raw);
          normalized.threat_detected = !Number.isNaN(num) && num > 0 ? 'Yes' : 'No';
        }
      } else if (normalized.message_threat_level != null) {
        // Derive a normalized threat flag from numeric level if provided.
        const level = Number(normalized.message_threat_level);
        if (!Number.isNaN(level)) {
          normalized.threat_detected = level > 0 ? 'Yes' : 'No';
        }
      }

      // Demo/debug behavior: if we STILL have no threat_detected flag at all and we're not in production,
      // mark a small, deterministic subset of vessels as threats so the map's threat visualization
      // can be exercised even with all-zero threat levels.
      if (normalized.threat_detected == null && process.env.NODE_ENV !== 'production') {
        normalized.threat_detected = (index % 15 === 0) ? 'Yes' : 'No';
      }

      // Ensure bearings and velocity for velocity vectors and threat trajectories (Step 5)
      normalized.heading_deg = normalized.heading_deg ?? normalized.direction ?? null;
      normalized.speed_knots = normalized.speed_knots ?? normalized.speed ?? null;

      const uid = coalesceVesselUniqueIdentifier(normalized);
      if (uid != null) normalized.unique_identifier = uid;

      return normalized;
    });

      if (allData.length > 0) {
      // Helpful debug output: inspect one normalized row and its keys
      // to verify vessel identifiers and names are present.
      // eslint-disable-next-line no-console
      console.log('BlueTide loadAllData:', {
        datasource: datasourceName,
        keys: Object.keys(allData[0]),
        data: allData,
      });
    }



    return { allData, totalRows };

  } catch (error) {
    console.error(`Failed to load data for area ${selectedArea} (datasource: ${datasourceName}):`, error);
    return { allData: [], totalRows: 0 };
  }
};

/**
 * Gets available dates for a datasource using DISTINCT query.
 * Useful because most datasources have sparse data.
 * @param {string} datasourceName - The datasource name (e.g., 'surface_vessel_data')
 * @returns {Promise<{dates: Array<string>, error: string|null}>} Available dates in YYYY-MM-DD format
 */
export const getAvailableDates = async (datasourceName = 'surface_vessel_data') => {
  const tableId = getTableIdForDatasource(datasourceName);
  const schema = getSchemaForDatasource(datasourceName);

  const sql = `SELECT DISTINCT DATE(${schema.timestampField}) as date FROM \`${tableId}\` ORDER BY date`;

  try {
    if (!API_CONFIG.baseUrl) {
      return { dates: [], error: 'REACT_APP_BASE_URL is not set' };
    }

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: buildDataProxyRequestHeaders(),
      body: JSON.stringify({ sql })
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.detail || response.statusText);
    }

    const apiData = await response.json();
    const dates = (apiData.rows || []).map(row => row.date);

    return { dates, error: null };
  } catch (error) {
    console.error(`Failed to get available dates for ${datasourceName}:`, error);
    return { dates: [], error: error.message };
  }
};

/**
 * Gets distinct common_name (vessel category) values for a datasource.
 * Uses the same data-proxy endpoint with SELECT DISTINCT common_name.
 * Useful for filters/dropdowns (e.g. vessel type). Only applies to datasources that have common_name (e.g. surface_vessel_data).
 * @param {string} datasourceName - The datasource name (e.g., 'surface_vessel_data')
 * @returns {Promise<{commonNames: Array<string>, error: string|null}>} Sorted list of category names (e.g. "fishing_boat", "cruise_ship")
 */
export const getAvailableVesselCommonNames = async (datasourceName = 'surface_vessel_data') => {
  const schema = getSchemaForDatasource(datasourceName);
  const commonNameCol = schema.fieldMap?.common_name || 'common_name';
  const tableId = getTableIdForDatasource(datasourceName);

  const sql = `SELECT DISTINCT \`${commonNameCol}\` as common_name FROM \`${tableId}\` WHERE \`${commonNameCol}\` IS NOT NULL AND TRIM(\`${commonNameCol}\`) != '' ORDER BY common_name`;

  try {
    if (!API_CONFIG.baseUrl) {
      return { commonNames: [], error: 'REACT_APP_BASE_URL is not set' };
    }

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: buildDataProxyRequestHeaders(),
      body: JSON.stringify({ sql })
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.detail || response.statusText);
    }

    const apiData = await response.json();
    const commonNames = (apiData.rows || []).map(row => row.common_name).filter(Boolean);

    return { commonNames, error: null };
  } catch (error) {
    console.error(`Failed to get common_name list for ${datasourceName}:`, error);
    return { commonNames: [], error: error.message };
  }
};

/**
 * Checks API connectivity by sending a lightweight query
 * @returns {Promise<boolean>} True if connected, false otherwise
 */
export const checkConnection = async () => {
  try {
    if (!API_CONFIG.baseUrl) return false;

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`;
    // Use a minimal query that should always work if the API is up
    const sql = "SELECT 1";

    const response = await fetch(url, {
      method: 'POST',
      headers: buildDataProxyRequestHeaders(),
      body: JSON.stringify({ sql })
    });

    return response.ok;
  } catch (error) {
    console.warn('API connection check failed:', error);
    return false;
  }
};

/**
 * Processes raw data into a format suitable for time series charts.
 * @param {Array} rawData - The raw data from the API.
 * @param {number} selectedDepth - The depth to filter the data by.
 * @param {number|null} maxDataPoints - Maximum number of data points to return (null = no limit).
 * @returns {Array} An array of processed data points for visualization.
 */
export const processAPIData = (rawData, selectedDepth = 0, maxDataPoints = null) => {
  if (!rawData || rawData.length === 0) {
    return [];
  }
  let filteredData = rawData.filter(row => {
    if (row.nspeed === null || row.nspeed === undefined || row.nspeed === '') {
      return false;
    }
    if (row.depth !== undefined && row.depth !== null && selectedDepth !== undefined) {
      const depthDiff = Math.abs(row.depth - selectedDepth);
      return depthDiff <= 5;
    }
    return true;
  });

  filteredData.sort((a, b) => {
    if (!a.time || !b.time) return 0;
    return new Date(a.time) - new Date(b.time);
  });

  const recentData = maxDataPoints ?
    filteredData.slice(-maxDataPoints) :
    filteredData;

  const processedData = recentData.map((row, index) => ({
    depth: row.depth || 0,
    time: formatTimeForDisplay(row.time),
    timestamp: row.time ? new Date(row.time) : new Date(),
    heading: row.direction || 0,
    currentSpeed: row.nspeed || 0,
    soundSpeed: row.sound_speed_ms || 0,
    waveHeight: row.ssh || 0,
    temperature: row.temp || null,
    latitude: row.lat,
    longitude: row.lon,
    salinity: row.salinity || null,
    pressure: row.pressure_dbars || null,
    sourceFile: row._source_file,
    model: row.model,
    area: row.area,
  }));

  return processedData;
};

/**
 * Generic processor for scalar data (like temperature, salinity) for heatmap generation.
 * @param {Array} rawData - The raw data from the API.
 * @param {string} parameterKey - The key of the parameter to process (e.g., 'temp', 'salinity').
 * @param {Object} options - Processing options.
 * @returns {Array} Array of processed scalar data points.
 */
const processScalarData = (rawData, parameterKey, options = {}) => {
  const { maxDataPoints = null, latestOnly = false, depthFilter = null } = options;

  if (!rawData || rawData.length === 0) return [];

  let filteredData = rawData.filter(row =>
    row.lat && row.lon &&
    row[parameterKey] !== null && row[parameterKey] !== undefined &&
    !isNaN(row.lat) && !isNaN(row.lon) && !isNaN(row[parameterKey]) &&
    Math.abs(row.lat) <= 90 && Math.abs(row.lon) <= 180
  );

  if (depthFilter !== null) {
    filteredData = filteredData.filter(row =>
      row.depth !== null && row.depth !== undefined && Math.abs(row.depth - depthFilter) <= 5
    );
  }

  filteredData.sort((a, b) => new Date(a.time) - new Date(b.time));

  if (latestOnly) {
    const latestData = new Map();
    filteredData.forEach(row => {
      const key = `${row.lat.toFixed(4)},${row.lon.toFixed(4)}`;
      if (!latestData.has(key) || new Date(row.time) > new Date(latestData.get(key).time)) {
        latestData.set(key, row);
      }
    });
    filteredData = Array.from(latestData.values());
  }

  if (maxDataPoints) {
    filteredData = filteredData.slice(-maxDataPoints);
  }

  return filteredData.map(row => ({
    latitude: row.lat,
    longitude: row.lon,
    value: row[parameterKey],
    time: row.time,
    depth: row.depth || 0,
  }));
};

/**
 * Processes vector data for map visualization with configurable field mapping
 * @param {Array} rawData - The raw data from the API
 * @param {Object} options - Processing options
 * @param {string} options.magnitudeKey - Field name for magnitude/speed
 * @param {string} options.directionKey - Field name for direction
 * @returns {Array} Array of vector data points
 */
export const processVectorData = (rawData, options = {}) => {
  const {
    maxDataPoints = null,
    latestOnly = false,
    gridResolution = 0.01,
    depthFilter = null,
    magnitudeKey = 'nspeed',
    directionKey = 'direction'
  } = options;

  if (!rawData || rawData.length === 0) return [];

  let vectorData = rawData.filter(row => {
    const magnitude = row[magnitudeKey];
    const direction = row[directionKey];
    return row.lat && row.lon &&
      magnitude !== null && magnitude !== undefined && !isNaN(magnitude) &&
      direction !== null && direction !== undefined && !isNaN(direction) &&
      Math.abs(row.lat) <= 90 && Math.abs(row.lon) <= 180;
  });

  if (depthFilter !== null) {
    vectorData = vectorData.filter(row =>
      row.depth !== null && row.depth !== undefined && Math.abs(row.depth - depthFilter) <= 5
    );
  }

  vectorData.sort((a, b) => new Date(a.time) - new Date(b.time));

  if (gridResolution > 0) {
    const gridData = new Map();
    vectorData.forEach(row => {
      const gridLat = Math.round(row.lat / gridResolution) * gridResolution;
      const gridLon = Math.round(row.lon / gridResolution) * gridResolution;
      const key = `${gridLat},${gridLon}`;
      if (!gridData.has(key)) {
        gridData.set(key, { lat: gridLat, lon: gridLon, directions: [], magnitudes: [], times: [], depths: [] });
      }
      const cell = gridData.get(key);
      cell.directions.push(row[directionKey]);
      cell.magnitudes.push(row[magnitudeKey] || 0);
      cell.times.push(row.time);
      cell.depths.push(row.depth || 0);
    });

    vectorData = Array.from(gridData.values()).map(cell => ({
      lat: cell.lat,
      lon: cell.lon,
      direction: calculateCircularMean(cell.directions),
      magnitude: cell.magnitudes.reduce((s, v) => s + v, 0) / cell.magnitudes.length,
      time: cell.times.sort((a, b) => new Date(b) - new Date(a))[0],
      depth: cell.depths.reduce((s, d) => s + d, 0) / cell.depths.length,
    }));
  }

  if (latestOnly) {
    const latestData = new Map();
    vectorData.forEach(row => {
      const key = `${row.lat.toFixed(4)},${row.lon.toFixed(4)}`;
      if (!latestData.has(key) || new Date(row.time) > new Date(latestData.get(key).time)) {
        latestData.set(key, row);
      }
    });
    vectorData = Array.from(latestData.values());
  }

  if (maxDataPoints) {
    vectorData = vectorData.slice(-maxDataPoints);
  }

  return vectorData.map((row, index) => ({
    id: `vector_${index}`,
    latitude: row.lat,
    longitude: row.lon,
    direction: row.direction,
    speed: row.magnitude,
    magnitude: row.magnitude,
    time: row.time,
    depth: row.depth || 0,
    coordinates: [row.lon, row.lat],
    vectorX: Math.sin((row.direction * Math.PI) / 180),
    vectorY: Math.cos((row.direction * Math.PI) / 180),
  }));
};

/**
 * Legacy function for backwards compatibility
 */
export const processCurrentsData = (rawData, options = {}) => {
  return processVectorData(rawData, {
    ...options,
    magnitudeKey: 'nspeed',
    directionKey: 'direction'
  });
};

/**
 * Calculates circular mean for directional data (angles in degrees)
 * @param {Array} angles - Array of angles in degrees
 * @returns {number} Circular mean in degrees
 */
const calculateCircularMean = (angles) => {
  if (angles.length === 0) return 0;
  let sumSin = 0;
  let sumCos = 0;

  angles.forEach(angle => {
    const radians = (angle * Math.PI) / 180;
    sumSin += Math.sin(radians);
    sumCos += Math.cos(radians);
  });

  const meanRadians = Math.atan2(sumSin / angles.length, sumCos / angles.length);
  let meanDegrees = (meanRadians * 180) / Math.PI;
  if (meanDegrees < 0) meanDegrees += 360;
  return meanDegrees;
};

/**
 * Generates vector data optimized for Mapbox visualization with configurable field mapping
 * @param {Array} rawData - The raw data from the API
 * @param {Object} options - Generation options
 * @returns {Object} GeoJSON-like object for Mapbox vector layers
 */
export const generateCurrentsVectorData = (rawData, options = {}) => {
  const {
    vectorScale = 0.009,
    minMagnitude = 0,
    colorBy = 'speed',
    maxVectors = 1000,
    depthFilter = null,
    displayParameter = 'Current Speed',
    magnitudeKey = 'nspeed',
    directionKey = 'direction'
  } = options;

  // Get field mappings based on display parameter
  const fieldMapping = getFieldMapping(displayParameter);
  const finalMagnitudeKey = magnitudeKey || fieldMapping.magnitudeKey;
  const finalDirectionKey = directionKey || fieldMapping.directionKey;

  // console.log('dataService - Field mapping:', { displayParameter, fieldMapping, finalMagnitudeKey, finalDirectionKey });
  // console.log('dataService - Raw data sample:', rawData?.[0]);

  const vectorData = processVectorData(rawData, {
    latestOnly: true,
    maxDataPoints: maxVectors,
    gridResolution: 0.01,
    depthFilter,
    magnitudeKey: finalMagnitudeKey,
    directionKey: finalDirectionKey
  });

  // console.log('dataService - Processed vectors:', vectorData.length);
  // console.log('dataService - Sample vector:', vectorData[0]);

  if (vectorData.length === 0) return { type: 'FeatureCollection', features: [] };

  const filteredVectors = vectorData.filter(vector => vector.magnitude >= minMagnitude);
  const speeds = filteredVectors.map(c => c.speed);
  const depths = filteredVectors.map(c => c.depth);
  const maxSpeed = Math.max(...speeds), minSpeed = Math.min(...speeds);
  const maxDepth = Math.max(...depths), minDepth = Math.min(...depths);

  const features = filteredVectors.map(vector => {
    const vectorLength = vector.magnitude * vectorScale;
    const endLat = vector.latitude + (vector.vectorY * vectorLength);
    const endLon = vector.longitude + (vector.vectorX * vectorLength);

    let colorValue = 0.5;
    if (colorBy === 'speed' && maxSpeed > minSpeed) {
      colorValue = (vector.speed - minSpeed) / (maxSpeed - minSpeed);
    } else if (colorBy === 'depth' && maxDepth > minDepth) {
      colorValue = (vector.depth - minDepth) / (maxDepth - minDepth);
    }

    return {
      type: 'Feature',
      properties: {
        id: vector.id,
        direction: vector.direction,
        speed: vector.speed,
        magnitude: vector.magnitude,
        depth: vector.depth,
        time: vector.time,
        colorValue: colorValue,
        dataPointCount: vector.dataPointCount || 1
      },
      geometry: {
        type: 'LineString',
        coordinates: [[vector.longitude, vector.latitude], [endLon, endLat]]
      }
    };
  });

  // console.log('dataService - Generated features:', features.length);
  // console.log('dataService - Sample feature:', features[0]);

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      vectorCount: features.length,
      speedRange: { min: minSpeed, max: maxSpeed },
      depthRange: { min: minDepth, max: maxDepth },
      colorBy: colorBy,
      displayParameter: displayParameter,
      fieldMapping: { magnitudeKey: finalMagnitudeKey, directionKey: finalDirectionKey }
    }
  };
};

/**
 * Gets field mapping for different layer types
 * @param {string} displayParameter - The display parameter name
 * @returns {Object} Field mapping configuration
 */
const getFieldMapping = (displayParameter) => {
  const mappings = {
    'Current Speed': { magnitudeKey: 'nspeed', directionKey: 'direction' },
    'Current Direction': { magnitudeKey: 'nspeed', directionKey: 'direction' },
    'Wind Speed': { magnitudeKey: 'nspeed', directionKey: 'ndirection' }, // Using normalized direction for wind
    'Wind Direction': { magnitudeKey: 'nspeed', directionKey: 'ndirection' }, // Using normalized direction for wind
    'Wave Direction': { magnitudeKey: 'nspeed', directionKey: 'direction' }, // Using wind speed with current direction as proxy
    'Ocean Currents': { magnitudeKey: 'nspeed', directionKey: 'direction' } // Default
  };

  return mappings[displayParameter] || mappings['Ocean Currents'];
};

/**
 * Gets currents color scale configuration for visualization
 * @param {Array} currentsData - Currents data for scale calculation
 * @param {string} colorBy - Property to base colors on ('speed', 'depth')
 * @returns {Object} Color scale configuration
 */
export const getCurrentsColorScale = (currentsData = [], colorBy = 'speed') => {
  if (currentsData.length === 0) return { min: 0, max: 10, property: colorBy, colors: [] };
  const values = currentsData.map(d => colorBy === 'speed' ? d.speed : d.depth).filter(v => !isNaN(v));
  const minValue = Math.min(...values), maxValue = Math.max(...values), midValue = (minValue + maxValue) / 2;
  return {
    min: minValue, max: maxValue, mid: midValue, property: colorBy,
    gradient: colorBy === 'speed'
      ? ['rgb(0, 0, 255)', 'rgb(0, 255, 0)', 'rgb(255, 0, 0)']
      : ['rgb(255, 255, 0)', 'rgb(0, 255, 255)', 'rgb(0, 0, 255)']
  };
};

/**
 * Generic generator for heatmap data from any scalar parameter.
 * @param {Array} rawData - The raw data from the API.
 * @param {string} parameterKey - The key of the data to visualize (e.g., 'temp', 'salinity').
 * @param {Object} options - Heatmap generation options.
 * @returns {Array} Array of [lat, lng, intensity] points for heatmap.
 */
const generateScalarHeatmapData = (rawData, parameterKey, options = {}) => {
  const { intensityScale = 1.0, normalize = true, gridResolution = 0.01, depthFilter = null } = options;
  const processedData = processScalarData(rawData, parameterKey, { latestOnly: false, depthFilter });

  if (processedData.length === 0) return [];

  const values = processedData.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal;

  const gridData = new Map();
  processedData.forEach(point => {
    const gridLat = Math.round(point.latitude / gridResolution) * gridResolution;
    const gridLng = Math.round(point.longitude / gridResolution) * gridResolution;
    const key = `${gridLat},${gridLng}`;
    if (!gridData.has(key)) {
      gridData.set(key, { lat: gridLat, lng: gridLng, values: [] });
    }
    gridData.get(key).values.push(point.value);
  });

  return Array.from(gridData.values()).map(cell => {
    const avgValue = cell.values.reduce((sum, val) => sum + val, 0) / cell.values.length;
    let intensity = normalize && range > 0 ? (avgValue - minVal) / range : avgValue * intensityScale;
    return [cell.lat, cell.lng, Math.max(0, Math.min(1, intensity))];
  });
};

export const generateTemperatureHeatmapData = (rawData, options) => generateScalarHeatmapData(rawData, 'temp', options);
export const generateSalinityHeatmapData = (rawData, options) => generateScalarHeatmapData(rawData, 'salinity', options);
export const generateSshHeatmapData = (rawData, options) => generateScalarHeatmapData(rawData, 'ssh', options);
export const generatePressureHeatmapData = (rawData, options) => generateScalarHeatmapData(rawData, 'pressure_dbars', options);


/**
 * Gets temperature color scale configuration for visualization
 * @param {Array} temperatureData - Temperature data for scale calculation
 * @returns {Object} Color scale configuration
 */
export const getTemperatureColorScale = (temperatureData = []) => {
  const temperatures = temperatureData.map(d => d.temperature).filter(t => !isNaN(t));

  if (temperatures.length === 0) {
    return {
      min: 0, max: 30, colors: [
        { value: 0, color: '#0000FF' }, { value: 0.25, color: '#00FFFF' },
        { value: 0.5, color: '#00FF00' }, { value: 0.75, color: '#FFFF00' },
        { value: 1.0, color: '#FF0000' }
      ]
    };
  }

  const minTemp = Math.min(...temperatures);
  const maxTemp = Math.max(...temperatures);
  const midTemp = (minTemp + maxTemp) / 2;
  const quarterTemp = minTemp + (maxTemp - minTemp) * 0.25;
  const threeQuarterTemp = minTemp + (maxTemp - minTemp) * 0.75;

  return {
    min: minTemp, max: maxTemp, mid: midTemp,
    colors: [
      { value: minTemp, color: '#0000FF' }, { value: quarterTemp, color: '#00FFFF' },
      { value: midTemp, color: '#00FF00' }, { value: threeQuarterTemp, color: '#FFFF00' },
      { value: maxTemp, color: '#FF0000' }
    ],
    gradient: ['rgb(0, 0, 255)', 'rgb(0, 255, 255)', 'rgb(0, 255, 0)', 'rgb(255, 255, 0)', 'rgb(255, 0, 0)']
  };
};

/**
 * Gets latest temperature readings grouped by location
 * @param {Array} rawData - The raw data from the API
 * @param {number} maxPoints - Maximum points to return
 * @returns {Array} Latest temperature readings per location
 */
export const getLatestTemperatureReadings = (rawData, maxPoints = 1000) => {
  const tempData = processScalarData(rawData, 'temp', {
    latestOnly: true, maxDataPoints: maxPoints
  });

  return tempData.map(point => ({
    ...point,
    temperature: point.value,
    id: `temp_${point.latitude}_${point.longitude}`,
    displayTemp: `${point.value.toFixed(1)}°C`,
    coordinates: [point.longitude, point.latitude]
  }));
};

/**
 * Formats a timestamp for display in charts.
 * @param {string | Date} time - The time value to format.
 * @returns {string} The formatted time string (HH:MM).
 */
export const formatTimeForDisplay = (time) => {
  if (!time) return '00:00';
  try {
    return new Date(time).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  } catch (error) {
    return '00:00';
  }
};

/**
 * Simple land/water detection using basic geographic rules
 */
export const isLikelyOnWater = (lat, lon) => {
  // More permissive bounding box to ensure all relevant data is included.
  const gulfBounds = { north: 31, south: 28, east: -86, west: -91 };
  if (lat >= gulfBounds.south && lat <= gulfBounds.north && lon >= gulfBounds.west && lon <= gulfBounds.east) {
    // A more precise cutout for the Mississippi River Delta landmass
    if (lat > 29 && lat < 29.8 && lon > -90 && lon < -89) {
      return false;
    }
    return true; // It's within the general Gulf area and not on the delta.
  }
  return false; // It's outside our primary area of interest.
};

/**
 * Color stations based on data characteristics
 */
const getStationColor = (dataPoints) => {
  if (dataPoints.length === 0) return [128, 128, 128];
  if (dataPoints.length > 1000) return [255, 69, 0];
  if (dataPoints.length > 500) return [255, 140, 0];
  if (dataPoints.length > 100) return [255, 215, 0];
  if (dataPoints.length > 10) return [0, 191, 255];
  return [0, 255, 127];
};

/**
 * Rough water depth estimation
 */
const estimateWaterDepth = (lat, lon) => {
  const distanceFromCoast = Math.min(Math.abs(lat - 29.5), Math.abs(lon - (-90)));
  return Math.min(distanceFromCoast * 100, 3000);
};

/**
 * Enhanced station generation with water filtering and deployment filtering
 */
export const generateOptimizedStationDataFromAPI = (rawData) => {
  if (!rawData || rawData.length === 0) return [];

  const waterData = rawData.filter(row => {
    if (!row.lat || !row.lon || isNaN(row.lat) || isNaN(row.lon)) return false;
    if (Math.abs(row.lat) > 90 || Math.abs(row.lon) > 180) return false;
    if (!isLikelyOnWater(row.lat, row.lon)) return false;
    if (row.status && (row.status === 'pre-deployment' || row.status === 'post-recovery')) return false;
    return true;
  });

  const stations = new Map();
  const getOptimalPrecision = (dataCount) => {
    if (dataCount > 50000) return 1; if (dataCount > 10000) return 2;
    if (dataCount > 1000) return 3; return 4;
  };
  const precision = getOptimalPrecision(waterData.length);
  const area = waterData.length > 0 ? waterData[0].area : null;

  waterData.forEach((row, index) => {
    const key = `${row.lat.toFixed(precision)},${row.lon.toFixed(precision)}`;
    if (!stations.has(key)) {
      const groupData = waterData.filter(r =>
        Math.abs(r.lat - row.lat) < Math.pow(10, -precision) &&
        Math.abs(r.lon - row.lon) < Math.pow(10, -precision)
      );
      const centroidLat = groupData.reduce((sum, r) => sum + r.lat, 0) / groupData.length;
      const centroidLon = groupData.reduce((sum, r) => sum + r.lon, 0) / groupData.length;
      stations.set(key, {
        name: `Ocean Station ${stations.size + 1}`, coordinates: [centroidLon, centroidLat],
        exactLat: centroidLat, exactLon: centroidLon, type: 'ocean_station',
        color: getStationColor(groupData), dataPoints: 0, sourceFiles: new Set(),
        allDataPoints: [], deploymentStatus: 'active', waterDepth: estimateWaterDepth(centroidLat, centroidLon),
        model: null, datasource: area  // BlueTide datasources don't use models
      });
    }
    const station = stations.get(key);
    station.dataPoints++;
    if (row._source_file) station.sourceFiles.add(row._source_file);
    station.allDataPoints.push({ ...row, rowIndex: index });
  });

  return Array.from(stations.values()).map(station => ({
    ...station, sourceFiles: Array.from(station.sourceFiles)
  }));
};

/**
 * Validate if coordinates represent real ocean monitoring locations
 */
export const validateOceanStations = (stations) => {
  return stations.map(station => ({
    ...station,
    validation: {
      isOnWater: isLikelyOnWater(station.exactLat, station.exactLon),
      hasData: station.dataPoints > 0,
      isActive: station.deploymentStatus === 'active',
      dataQuality: station.dataPoints > 10 ? 'good' : 'limited'
    }
  }));
};

/**
 * Generates station locations from data by grouping nearby coordinates
 */
export const generateStationDataFromAPI = (rawData) => {
  if (!rawData || rawData.length === 0) return [];
  const stations = new Map();
  const area = rawData.length > 0 ? rawData[0].area : null;
  rawData.forEach((row, index) => {
    if (row.lat && row.lon && !isNaN(row.lat) && !isNaN(row.lon)) {
      const precision = 4;
      const key = `${row.lat.toFixed(precision)},${row.lon.toFixed(precision)}`;
      if (!stations.has(key)) {
        stations.set(key, {
          name: `Station at ${row.lat.toFixed(4)}, ${row.lon.toFixed(4)}`,
          coordinates: [row.lon, row.lat], exactLat: row.lat, exactLon: row.lon,
          type: 'api_station', color: [Math.random() * 255, Math.random() * 255, Math.random() * 255],
          dataPoints: 0, sourceFiles: new Set(), allDataPoints: [], model: null, datasource: area  // BlueTide datasources don't use models
        });
      }
      const station = stations.get(key);
      station.dataPoints++;
      if (row._source_file) station.sourceFiles.add(row._source_file);
      station.allDataPoints.push({ ...row, rowIndex: index });
    }
  });
  return Array.from(stations.values()).map(station => ({
    ...station, sourceFiles: Array.from(station.sourceFiles)
  }));
};

/**
 * Alternative version that creates individual stations for each unique coordinate
 */
export const generateStationDataFromAPINoGrouping = (rawData) => {
  if (!rawData || rawData.length === 0) return [];
  const stations = [];
  const seenCoordinates = new Set();
  const area = rawData.length > 0 ? rawData[0].area : null;
  rawData.forEach((row, index) => {
    if (row.lat && row.lon && !isNaN(row.lat) && !isNaN(row.lon)) {
      const coordKey = `${row.lat}_${row.lon}`;
      if (!seenCoordinates.has(coordKey)) {
        seenCoordinates.add(coordKey);
        stations.push({
          name: `Station ${stations.length + 1} (${row.lat.toFixed(4)}, ${row.lon.toFixed(4)})`,
          coordinates: [row.lon, row.lat], exactLat: row.lat, exactLon: row.lon,
          type: 'api_station', color: [Math.random() * 255, Math.random() * 255, Math.random() * 255],
          dataPoints: rawData.filter(r => r.lat === row.lat && r.lon === row.lon).length,
          sourceFiles: [...new Set(rawData.filter(r => r.lat === row.lat && r.lon === row.lon).map(r => r._source_file).filter(Boolean))],
          allDataPoints: rawData.filter(r => r.lat === row.lat && r.lon === row.lon),
          model: null, datasource: area  // BlueTide datasources don't use models
        });
      }
    }
  });
  return stations;
};

/**
 * Debug function to validate coordinate data
 */
export const validateCoordinateData = (rawData) => {
  const validCoords = rawData.filter(row =>
    row.lat && row.lon && !isNaN(row.lat) && !isNaN(row.lon) &&
    Math.abs(row.lat) <= 90 && Math.abs(row.lon) <= 180
  );
  const invalidCoords = rawData.filter(row =>
    !row.lat || !row.lon || isNaN(row.lat) || isNaN(row.lon) ||
    Math.abs(row.lat) > 90 || Math.abs(row.lon) > 180
  );
  return {
    total: rawData.length, valid: validCoords.length, invalid: invalidCoords.length,
    validPercentage: (validCoords.length / rawData.length * 100).toFixed(1),
    coordinateRanges: validCoords.length > 0 ? {
      latitude: {
        min: Math.min(...validCoords.map(r => r.lat)), max: Math.max(...validCoords.map(r => r.lat)),
        range: Math.max(...validCoords.map(r => r.lat)) - Math.min(...validCoords.map(r => r.lat))
      },
      longitude: {
        min: Math.min(...validCoords.map(r => r.lon)), max: Math.max(...validCoords.map(r => r.lon)),
        range: Math.max(...validCoords.map(r => r.lon)) - Math.min(...validCoords.map(r => r.lon))
      }
    } : null,
    sampleValidCoords: validCoords.slice(0, 10).map(r => ({ lat: r.lat, lon: r.lon })),
    sampleInvalidCoords: invalidCoords.slice(0, 5).map(r => ({ lat: r.lat, lon: r.lon }))
  };
};

const dataService = {
  loadAllData,
  getAvailableDates,
  getAvailableVesselCommonNames,
  getAvailableDatasources,
  processAPIData,
  processCurrentsData,
  processVectorData,
  generateCurrentsVectorData,
  getCurrentsColorScale,
  generateTemperatureHeatmapData,
  generateSalinityHeatmapData,
  generateSshHeatmapData,
  generatePressureHeatmapData,
  getTemperatureColorScale,
  getLatestTemperatureReadings,
  formatTimeForDisplay,
  generateOptimizedStationDataFromAPI,
  generateStationDataFromAPI,
  generateStationDataFromAPINoGrouping,
  validateOceanStations,
  validateCoordinateData,
  isLikelyOnWater
};

export default dataService;